// Ficha de línea, como el esquema de una línea de metro: elige sentido (y variante, si la
// línea tiene varios recorridos) y verás sus paradas en orden sobre una barra del color de la
// línea, con los minutos del próximo bus en cada una y el autobús dibujado entre las paradas
// por las que va ahora. El mapa muestra solo esta línea y resalta el recorrido elegido.

import type { BusRoute, Dataset, LineId } from "../data";
import type { LatLng } from "../geo";
import type { MapAdapter } from "../map/types";
import type { LiveTracker } from "../live/tracker";
import { fetchWarnings, type ApiWarning } from "../live/api";
import { badge, escapeHtml, minutesText, titleCase } from "./common";
import type { DetailView } from "./detail";

export interface LineViewOptions {
  ds: Dataset;
  adapter: MapAdapter;
  tracker: LiveTracker;
  bottomPad: () => number;
  showOnly: (line: LineId | null) => void;
  onOpenStop: (stopIdx: number) => void;
}

// Avisos del operador: una consulta cada 10 minutos como mucho.
let warnings: { at: number; list: ApiWarning[] } | null = null;
async function lineWarnings(line: LineId): Promise<ApiWarning[]> {
  if (!warnings || Date.now() - warnings.at > 600_000) {
    warnings = { at: Date.now(), list: await fetchWarnings() };
  }
  return warnings.list.filter((w) => w.line === line);
}

// Recorridos distintos (la API repite alguno), agrupados por sentido; el más largo primero.
function variantsBySense(routes: readonly BusRoute[]): Map<string, BusRoute[]> {
  const seen = new Set<string>();
  const out = new Map<string, BusRoute[]>();
  for (const r of routes) {
    const sig = `${r.sense}|${r.stops.map((s) => s.id).join(",")}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.set(r.sense, [...(out.get(r.sense) ?? []), r]);
  }
  for (const list of out.values()) list.sort((a, b) => b.stops.length - a.stops.length);
  return out;
}

// «25 paradas · por Aljub»: la primera parada que solo tiene esta variante del sentido.
function variantLabel(ds: Dataset, r: BusRoute, siblings: readonly BusRoute[]): string {
  // Por nombre: dos postes de la misma plaza (uno por variante) no distinguen nada.
  const nameOf = (id: number): string => ds.stopsById.get(id)?.name ?? "";
  const others = new Set(siblings.filter((x) => x !== r).flatMap((x) => x.stops.map((s) => nameOf(s.id))));
  const name = r.stops.map((s) => nameOf(s.id)).find((n) => n && !others.has(n));
  return `${r.stops.length} paradas${name ? ` · por ${titleCase(name)}` : ""}`;
}

export function lineView(o: LineViewOptions, lineId: LineId, startRoute?: number): DetailView {
  let routeId = startRoute;
  return {
    mount(el, back) {
      const line = o.ds.linesById.get(lineId);
      const bySense = variantsBySense(o.ds.routes.get(lineId) ?? []);
      const routes = [...bySense.values()].flat();
      if (!line || routes.length === 0) {
        back();
        return () => {};
      }
      if (!routes.some((r) => r.id === routeId)) routeId = routes[0]!.id;
      o.showOnly(lineId);

      // Encuadre del recorrido dejando sitio al panel. Tras pintar la ficha: antes el panel aún
      // no tiene su alto y la línea quedaría debajo.
      const fit = (path: readonly LatLng[]): void => {
        o.adapter.fitBounds(
          {
            south: Math.min(...path.map((p) => p.lat)),
            north: Math.max(...path.map((p) => p.lat)),
            west: Math.min(...path.map((p) => p.lon)),
            east: Math.max(...path.map((p) => p.lon)),
          },
          { top: 40, right: 40, left: 40, bottom: o.bottomPad() + 20 },
        );
      };

      el.innerHTML = `
        <div class="dhead">
          <button type="button" class="back" aria-label="Volver">‹</button>
          ${badge(line.id, line.color, "l")}
          <div><h2>${escapeHtml(line.name)}</h2><small>Tiempo real cada 30 s</small></div>
        </div>
        <div class="warn" hidden></div>
        <div class="senses">${[...bySense.values()]
          .map((list) => `<button type="button" data-route="${list[0]!.id}">Hacia ${escapeHtml(titleCase(list[0]!.destination))}</button>`)
          .join("")}</div>
        <div class="variants" hidden></div>
        <ol class="diagram" style="--c:${line.color}"></ol>`;
      el.querySelector(".back")!.addEventListener("click", back);

      const diagram = el.querySelector<HTMLOListElement>(".diagram")!;
      const variantsEl = el.querySelector<HTMLDivElement>(".variants")!;
      let shownRoute: number | null = null;
      const render = (): void => {
        const r = routes.find((x) => x.id === routeId)!;
        const siblings = bySense.get(r.sense) ?? [r];
        el.querySelectorAll<HTMLButtonElement>(".senses button").forEach((b) =>
          b.classList.toggle("act", siblings.some((x) => x.id === Number(b.dataset["route"]))),
        );
        variantsEl.hidden = siblings.length < 2;
        variantsEl.innerHTML = siblings
          .map(
            (x) =>
              `<button type="button" data-route="${x.id}" class="${x.id === r.id ? "act" : ""}">${escapeHtml(variantLabel(o.ds, x, siblings))}</button>`,
          )
          .join("");
        // Recorrido elegido resaltado sobre la red (solo al cambiar, no en cada refresco).
        if (shownRoute !== r.id) {
          shownRoute = r.id;
          window.requestAnimationFrame(() => fit(r.path));
          o.adapter.drawRoute({
            legs: [{ type: "bus", line: lineId, color: line.color, path: r.path, boardIdx: 0, alightIdx: r.stops.length - 1 }],
          });
        }
        const buses = o.tracker.busesOn(lineId, r.id);
        const rows: string[] = [];
        r.stops.forEach((s, k) => {
          const stop = o.ds.stopsById.get(s.id);
          const min = o.tracker.nextAt(s.id, lineId, r.id);
          const here = buses.some((b) => b.atStopId === s.id);
          const eta = here
            ? `<b class="eta now">En parada</b>`
            : min === null
              ? ""
              : `<b class="eta${min <= 1 ? " now" : ""}">${minutesText(min)}</b>`;
          const end = k === 0 || k === r.stops.length - 1 ? " end" : "";
          rows.push(`<li class="st${end}" data-idx="${stop?.idx ?? ""}">
            <i class="dot"></i><span>${escapeHtml(titleCase(stop?.name ?? String(s.id)))}<small>${s.id}</small></span>${eta}</li>`);
          if (buses.some((b) => b.fromStopId === s.id && b.atStopId === null)) {
            rows.push(`<li class="bus">${badge(line.id, line.color, "s")}<span>Autobús en camino</span></li>`);
          }
        });
        diagram.innerHTML = rows.join("");
      };
      const pick = (e: Event): void => {
        const id = (e.target as HTMLElement).closest<HTMLElement>("[data-route]")?.dataset["route"];
        if (!id) return;
        routeId = Number(id);
        render();
      };
      el.querySelector(".senses")!.addEventListener("click", pick);
      variantsEl.addEventListener("click", pick);
      diagram.addEventListener("click", (e) => {
        const idx = (e.target as HTMLElement).closest<HTMLElement>("li.st")?.dataset["idx"];
        if (idx) o.onOpenStop(Number(idx));
      });
      render();
      const off = o.tracker.onUpdate(render);

      let alive = true;
      void lineWarnings(lineId)
        .then((ws) => {
          const box = el.querySelector<HTMLDivElement>(".warn");
          if (!alive || !box || ws.length === 0) return;
          box.innerHTML = ws.flatMap((w) => w.messages).map((m) => `<p>⚠️ ${escapeHtml(m)}</p>`).join("");
          box.hidden = false;
        })
        .catch(() => {});

      return () => {
        alive = false;
        off();
        o.adapter.drawRoute(null);
        o.showOnly(null);
      };
    },
  };
}
